/**
 * OraChurnSave — v1.4 churn-save offer card.
 *
 * Rendered when the backend's /api/oracle/recommend response includes a
 * non-null `churn_save_offer` field (priority over stall_tap — both can't
 * fire at once; backend ensures churn wins).
 *
 * Visual: heavier weight than a stall tap (this is a more important
 * relationship moment), softer than an alert (no urgency theater). The
 * three frequencies render with distinct visual tones:
 *
 *   F1 / REGRET  — gold accent, honest loss frame
 *   F2 / DESIRE  — gold accent, feature-discovery framing
 *   F3 / HONEST  — muted/grey, explicit cancel + "we'll stop reaching out"
 *
 * Both CTAs (primary save + secondary cancel) post telemetry events so the
 * backend can:
 *   - Close the cascade (no more frequencies will fire for this churn event)
 *   - Attribute the outcome (kept / paused / switched / canceled)
 *
 * Lane discipline: never mentions tickers or market opinions. Templates
 * are static server-side; no LLM in the churn path.
 */
import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import api from "@/utils/api";

export default function OraChurnSave({ offer, theme }) {
  const navigate = useNavigate();
  if (!offer) return null;

  function postAction(outcome) {
    // outcome: 'kept' | 'paused' | 'switched' | 'canceled'
    api.post('/oracle/telemetry', {
      event: "churn_save_action",
      rec_template_id: offer.template_id,
      outcome_event: outcome,
    }).catch(() => {});
  }

  function handlePrimary() {
    // Primary CTA — for F1/F2 this is the "stay" action (kept). For F3 it's cancel.
    const outcome = offer.is_final ? "canceled" : "kept";
    postAction(outcome);
    navigateTo(offer.cta_path);
  }

  function handleSecondary() {
    // Secondary — for F1/F2 this is "I want to leave anyway". For F3 it's "actually stay".
    const outcome = offer.is_final ? "kept" : "canceled";
    postAction(outcome);
    navigateTo(offer.secondary_cta_path);
  }

  function navigateTo(path) {
    if (!path) return;
    if (path.startsWith("http")) {
      window.location.href = path;
    } else {
      navigate(path);
    }
  }

  const isFinal = !!offer.is_final;
  const accent = theme?.primary || "#C9A227";
  const accentSoft = theme?.accent || "#E6C04A";

  return (
    <div
      data-testid="ora-churn-save"
      data-frequency={offer.frequency}
      data-is-final={isFinal}
      style={{
        marginTop: 16,
        marginBottom: 18,
        padding: "20px 22px",
        background: isFinal
          ? "linear-gradient(140deg, rgba(245,242,233,0.05), rgba(245,242,233,0.02))"
          : `linear-gradient(140deg, ${accent}1a, ${accentSoft}08)`,
        border: `1px solid ${isFinal ? "rgba(245,242,233,0.22)" : accent + "66"}`,
        borderRadius: 14,
      }}
    >
      <div
        style={{
          fontSize: "0.6rem",
          fontWeight: 800,
          letterSpacing: "0.22em",
          color: isFinal ? (theme?.textSecondary || "#A8A294") : accent,
          marginBottom: 8,
          textTransform: "uppercase",
        }}
      >
        ORA · {offer.label}
      </div>
      <div
        style={{
          fontSize: "1.1rem",
          fontWeight: 700,
          color: theme?.textPrimary || "#F5F2E9",
          fontFamily: "Space Grotesk, sans-serif",
          marginBottom: 8,
          lineHeight: 1.3,
        }}
      >
        {offer.headline}
      </div>
      <div
        style={{
          fontSize: "0.88rem",
          color: theme?.textSecondary || "#A8A294",
          lineHeight: 1.6,
          marginBottom: 16,
        }}
      >
        {offer.body}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <button
          onClick={handlePrimary}
          data-testid={`ora-churn-save-primary-${offer.template_id}`}
          style={{
            background: isFinal
              ? "transparent"
              : `linear-gradient(135deg, ${accent}, ${accentSoft})`,
            color: isFinal ? (theme?.textPrimary || "#F5F2E9") : "#0A0A0B",
            border: isFinal ? `1px solid ${accent}66` : "none",
            borderRadius: 10,
            padding: "12px 18px",
            fontWeight: 700,
            fontSize: "0.9rem",
            cursor: "pointer",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 6,
          }}
        >
          {offer.cta_text}
          {!isFinal && <ArrowRight className="w-4 h-4" />}
        </button>
        <button
          onClick={handleSecondary}
          data-testid={`ora-churn-save-secondary-${offer.template_id}`}
          style={{
            background: "transparent",
            color: theme?.textSecondary || "#A8A294",
            border: "none",
            padding: "8px 12px",
            fontSize: "0.78rem",
            cursor: "pointer",
            textDecoration: "underline",
            textUnderlineOffset: 3,
          }}
        >
          {offer.secondary_cta_text}
        </button>
      </div>
    </div>
  );
}
